import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/providers/AuthProvider";
import { getRedirectToken } from "@/services/auth.service";
import { USER_PROFILE_DROPDOWN } from "@/utils/constants/appConstants/commonConstants";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import { getAllOrganizations } from "@/utils/functions/OrganizationFunctions";
import { Organization } from "@/utils/interfaces/organization.interface";
import UserInfoCard from "@/components/cards/UserInfoCard";
import { USER_PORTAL_ADDRESS } from "@/constants/allconstants";
import { getFromLocalStorage } from "@/utils/functions/helperFunctions";
import {
    DropdownMenuContent,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";

interface ProfileDropdownProps {
    orgConfig: any;
    onSwitchProfile: () => void;
}

const ProfileDropdown = (props: ProfileDropdownProps) => {
    const navigate = useNavigate();
    const { logOut } = useAuth();
    const isUserPortalAccessPermitted = useFeaturePermission(FEATURE_PERMISSIONS.UserPortalAccess);
    const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);

    useEffect(() => {
        setAllOrganizations(getAllOrganizations());
    }, []);

    const switchOrganizationClicked = () => {
        if (allOrganizations?.length > 1) {
            props.onSwitchProfile();
        }
    };

    const redirectToUserPortal = async (e: Event) => {
        e.preventDefault();
        try {
            const response = await getRedirectToken();
            window.location.href = `${USER_PORTAL_ADDRESS}auth/${response.redirect_hash}?sso=${getFromLocalStorage("loginWithSSO")}`;
        } catch {
            return;
        }
    };

    return (
        <DropdownMenuContent
                align="end"
                side="bottom"
                sideOffset={11}
                alignOffset={-8}
                className="gs-profile-dropdown p-0 bg-white"
            >
                <UserInfoCard id="navbar" />
                <div className="mx-2">
                    {allOrganizations?.length > 1 && (
                        <DropdownMenuItem
                            className="dropdown-item cursor-pointer"
                            id="navbar-dropdown-switch-profile"
                            onSelect={switchOrganizationClicked}
                        >
                            <div>
                                <em
                                    id="navbar-dropdown-switch-profile-option-icon"
                                    className="icon ni ni-reload mr-1 user_profile_icon"
                                />
                                <span id="navbar-dropdown-switch-profile-option">
                                    {USER_PROFILE_DROPDOWN.SWITCH_PROFILE}
                                </span>
                            </div>
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                        className="dropdown-item cursor-pointer"
                        id="navbar-dropdown-view-profile"
                        onSelect={() => navigate("/my-profile")}
                    >
                        <div>
                            <em
                                id="navbar-dropdown-view-profile-option-icon"
                                className="icon ni ni-user-list mr-1 user_profile_icon"
                            />
                            <span id="navbar-dropdown-view-profile-option">
                                {USER_PROFILE_DROPDOWN.VIEW_PROFILE}
                            </span>
                        </div>
                    </DropdownMenuItem>
                    {!(
                        props.orgConfig?.is_directory_service_enabled &&
                        props.orgConfig?.is_restrict_login_with_sso
                    ) && (
                        <DropdownMenuItem
                            className="dropdown-item cursor-pointer"
                            id="navbar-dropdown-change-password"
                            onSelect={() => navigate("/change-password")}
                        >
                            <div>
                                <em
                                    id="navbar-dropdown-change-password-option-icon"
                                    className="icon ni ni-lock mr-1 user_profile_icon"
                                />
                                <span id="navbar-dropdown-change-password-option">
                                    {USER_PROFILE_DROPDOWN.CHANGE_PASSWORD}
                                </span>
                            </div>
                        </DropdownMenuItem>
                    )}
                    {isUserPortalAccessPermitted && (
                        <DropdownMenuItem
                            className="dropdown-item cursor-pointer"
                            id="navbar-dropdown-user-portal"
                            onSelect={redirectToUserPortal}
                        >
                            <div>
                                <em
                                    id="navbar-dropdown-user-portal-option-icon"
                                    className="icon ni ni-users mr-1 user_profile_icon"
                                />
                                <span id="navbar-dropdown-user-portal-option">
                                    {USER_PROFILE_DROPDOWN.USER_PORTAL}
                                </span>
                            </div>
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                        className="dropdown-item border-top mb-1 cursor-pointer"
                        id="navbar-dropdown-sign-out"
                        onSelect={async () => await logOut()}
                    >
                        <div className="px-0">
                            <em
                                id="navbar-dropdown-sign-out-option-icon"
                                className="icon ni ni-signout user_profile_icon mr-1"
                            />
                            <span
                                id="navbar-dropdown-sign-out-option"
                                className="normal_text color-Black"
                            >
                                {USER_PROFILE_DROPDOWN.SIGN_OUT}
                            </span>
                        </div>
                    </DropdownMenuItem>
                </div>
            </DropdownMenuContent>
    );
};

export default ProfileDropdown;
